import { CURRENT_VERSION } from '~/data/version'
import { IS_NATIVE, PLATFORM } from '~/utils/platform'

/**
 * 📱 The one door to the game's API (M3, mobile gameplan). Every client call
 * - the 26 $fetch sites and the funnel beacon - names its route as a site
 * path ('/api/wallet') and goes through here. On the web the base is empty,
 * so the call is exactly the relative request it always was. In the app
 * bundles the page lives at capacitor://localhost or https://localhost, where
 * a relative '/api/...' would hit the bundle, so the base is the hosted API
 * (server/middleware/00.0.cors.ts lets those two origins in).
 *
 */
declare const __ED_API_BASE__: string | undefined

export const API_BASE = typeof __ED_API_BASE__ === 'string' ? __ED_API_BASE__ : ''

/** '/api/wallet' -> '/api/wallet' on the web, 'https://elementaldispatch.com/api/wallet' in the apps. */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`
}

/** $fetch with the API base in front - same options, same typed responses.
 * Looks up the global $fetch at call time (Nuxt installs it once the entry
 * runs; the unit suites install fakes). */
export const apiFetch = (async (request: string, opts?: Record<string, unknown>) => {
  const options = IS_NATIVE ? { ...opts, headers: new Headers(opts?.headers as HeadersInit | undefined) } : opts
  if (IS_NATIVE) {
    const headers = options!.headers as Headers
    headers.set('x-ed-client', CURRENT_VERSION)
    headers.set('x-ed-platform', PLATFORM)
  }
  try {
    return await $fetch(apiUrl(request), options)
  } catch (error) {
    const failure = error as { statusCode?: number; status?: number; response?: { status?: number }; data?: { data?: { minVersion?: string } } }
    if (IS_NATIVE && (failure?.statusCode ?? failure?.status ?? failure?.response?.status) === 426 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('elemental-dispatch-update-required', {
        detail: { minVersion: failure.data?.data?.minVersion },
      }))
    }
    if ((failure?.statusCode ?? failure?.status ?? failure?.response?.status) === 410 && typeof window !== 'undefined') {
      // The plugin owns cleanup. Carry only the request's UID so a delayed
      // response for an old account cannot sign out a new account on this device.
      let uid: string | undefined
      try {
        const token = new Headers(opts?.headers as HeadersInit | undefined).get('authorization')?.replace(/^Bearer\s+/i, '')
        const payload = token?.split('.')[1]
        const subject = payload && JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).sub
        if (typeof subject === 'string') uid = subject
      } catch { /* a missing/malformed header does not replace the API error */ }
      window.dispatchEvent(new CustomEvent('elemental-dispatch-account-deleted', { detail: { uid } }))
    }
    throw error
  }
}) as unknown as typeof $fetch
